import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {
  Button,
  Divider,
  Grid,
  TextField,
  styled,
  Icon,
  Icons,
} from '@nextiva/next-ui-components';
import {
  Controller,
  FormProvider,
  useForm,
  useFieldArray,
} from 'react-hook-form';
import { useQueries, useQueryClient } from 'react-query';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { getPhoneNumber } from 'services/PNOS/CampaignRegistryService';
import { SectionSaveCancel } from 'components/SectionSaveCancel';
import {
  DEFAULT_RING_PATTERN,
  MAX_ALTERNATE_NUMBERS,
} from 'constants/phone-number';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { PlatformDialog } from 'components/Dialog';
import { Hint } from 'components/PhoneNumberHint';
import { SelectSearchInput } from 'components/SelectSearchInput';
import { Skeleton } from 'components/Skeleton';
import { RhfSelect } from 'components/RhfSelect';
import TeamSMSToggle from 'containers/pages/TeamsMessaging/TeamSMSToggle';
import { useSnackbar, useTeamUpdateSMS } from 'hooks';
import { HuntGroupService, PhoneNumbersService } from 'services';
import { ContainerStyles } from 'styles/application/phone-number';
import { teamPhoneNumberSchema, formatToPhoneNumber } from 'utils/phone-number';
import { initialState, reducer, actionTypes } from './reducer';

const StyledDivider = styled(Divider)`
  margin-top: 65px;
`;

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.palette.common.black};
`;

export const TeamPhoneNumberContainer = ({ huntGroupId = '' }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const setSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsError, setSMSError] = useState('');

  const [
    {
      data: availableNumbersData,
      isLoading: availableNumbersLoading,
      remove: removeAvailableNumbers,
    },
    { data: ringPatternsData, isLoading: ringPatternsLoading },
  ] = useQueries([
    {
      queryKey: ['huntGroupAvailableNumbers', huntGroupId],
      queryFn: () => HuntGroupService.getHuntGroupAvailableNumbers(huntGroupId),
    },
    {
      queryKey: ['ringPatterns'],
      queryFn: PhoneNumbersService.getRingPatterns,
    },
  ]);

  const {
    mutateAsync: updateHuntGroupPhoneNumber,
    isLoading,
  } = useTeamUpdateSMS();

  const {
    featureState: { teamsSMS },
    corpAcctNbr,
  } = useContext(FeatureContext);

  const methods = useForm({
    shouldUnregister: false,
    defaultValues: {
      primaryPhoneNumber: {
        phoneNumber: '',
        extension: '',
        unreachableForwardingNumber: '',
      },
      alternatePhoneNumbers: [],
    },
    mode: 'onSubmit',
    resolver: yupResolver(teamPhoneNumberSchema()),
  });

  const { control, getValues, handleSubmit, reset } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'alternatePhoneNumbers',
  });

  const ringPatterns = useMemo(() => {
    if (!Array.isArray(state.ringPatterns)) return [];

    return state.ringPatterns.map((item) => ({
      key: item,
      id: item,
      label: item,
      value: item.replace(/-/gi, '_').toUpperCase(),
    }));
  }, [state.ringPatterns]);

  /*
   * Normally, you'd control whether to display to toggle the ability to save/cancel based on `Object.keys(formState.dirtyFields).length > 0`
   * But RHF has a hard time properly tracking when fields are actually in their original state when relying on `useFieldArray` to add dynamic fields.
   * So if a user edits a dynamicly created field, then un-edits it to return it back to its original value, RHF does not seem to properly track that the field is no longer dirty.
   * Since the ask for the design when creating this is to conditionally render the cancel/save buttons, a decent amount of extra logic goes in to ensuring that those buttons show properly via `determineCanSave()`
   * determineCanSave() checks the entire form for differences from its original state. Unsurprisingly, this is a slow process to compute even with `debounce`.
   */
  const determineCanSave = useMemo(
    () =>
      debounce((removeOnly) => {
        const { primaryPhoneNumber, alternatePhoneNumbers = [] } = getValues();

        dispatch({
          type: actionTypes.SET_CAN_SAVE,
          payload: { primaryPhoneNumber, alternatePhoneNumbers, removeOnly },
        });
      }, 200),
    [getValues],
  );

  // 90% of the time, the goal is to re-render the form fields using the updated `state.orig` values. The other 10%, we need to re-render specific fields that are passed as args
  const renderFormFields = (
    primaryNumberObj,
    alternateNumbersArr,
    removeOnly,
  ) => {
    reset({
      'primaryPhoneNumber.phoneNumber':
        primaryNumberObj?.phoneNumber ||
        state.origPrimaryPhoneNumber.phoneNumber ||
        '',
      'primaryPhoneNumber.extension':
        state.origPrimaryPhoneNumber.extension || '',
      'primaryPhoneNumber.unreachableForwardingNumber':
        primaryNumberObj?.unreachableForwardingNumber ||
        state.origPrimaryPhoneNumber.unreachableForwardingNumber ||
        '',
      alternatePhoneNumbers: alternateNumbersArr
        ? [...alternateNumbersArr]
        : [...state.origAlternatePhoneNumbers],
    });
    determineCanSave(removeOnly);
  };

  const addAdditionalNumberRow = () => {
    append({
      phoneNumber: '',
      extension: '',
      ringPattern: DEFAULT_RING_PATTERN,
    });
  };

  const getInitialData = () => {
    try {
      const {
        availableNumbers = [],
        forwardToPhoneNumber,
        locationId,
        primaryExtension,
        primaryPhoneNumber,
        huntGroupAlternatePhone: { alternateNumberList = [] },
      } = availableNumbersData;

      if (primaryPhoneNumber) {
        getPhoneNumber(corpAcctNbr, primaryPhoneNumber).then((response) => {
          setSmsEnabled(response.data?.metadata.smsEnabled);
        });
      }

      alternateNumberList.forEach((phoneObj) => {
        if (!phoneObj.extension) phoneObj.extension = '';
        if (!phoneObj.phoneNumber) phoneObj.phoneNumber = '';
      });

      const origPrimaryPhoneNumber = {
        phoneNumber: primaryPhoneNumber,
        extension: primaryExtension,
        unreachableForwardingNumber: forwardToPhoneNumber,
      };

      dispatch({
        type: actionTypes.SET_INITIAL_DATA,
        payload: {
          availableNumbers,
          locationId,
          origAlternatePhoneNumbers: alternateNumberList,
          origPrimaryPhoneNumber,
          ringPatterns: ringPatternsData,
        },
      });
    } catch (err) {
      setSnackbar({
        message:
          'There was an error while retrieving phone number information for this team. Please try again later or contact support if the issue persists.',
        severity: 'error',
      });
    } finally {
      dispatch({
        type: actionTypes.SET_IS_LOADING,
        payload: false,
      });
    }
  };

  // This is a logic to go through all the errors which are returned from the backend validation and compare to the current list of additional numbers and set the error based on their index .
  const setExtensionErrors = (listOfValidationErrors) => {
    const errorsList = [];
    listOfValidationErrors?.validationErrors.forEach((error) => {
      errorsList[error.fieldPath.index] = {
        error: error.errorMessage,
        index: error.fieldPath.index,
      };
    });

    dispatch({
      type: actionTypes.SET_EXTENSION_ERRORS,
      payload: errorsList,
    });
  };

  const handleSaveErrors = (errorObj) => {
    if (
      errorObj.httpStatus === 'BAD_REQUEST' &&
      errorObj.message?.startsWith('Improper Extension Length')
    ) {
      setSnackbar({ message: errorObj.message, severity: 'error' });
      return;
    } else if (
      errorObj.httpStatus === 'CONFLICT' &&
      errorObj.message?.startsWith('Extension is already assigned')
    ) {
      setSnackbar({ message: errorObj.message, severity: 'error' });
      return;
    }
    setExtensionErrors(errorObj);
  };

  const cancelChanges = () => {
    dispatch({ type: actionTypes.RESET_AVAILABLE_NUMBERS });
    renderFormFields();
    dispatch({
      type: actionTypes.SET_EXTENSION_ERRORS,
      payload: [],
    });
  };

  const showSMSModal = (phoneNumber) => {
    if (phoneNumber === '') {
      dispatch({
        type: actionTypes.SET_DELETE_PRIMARY_SMS,
        payload: true,
      });
    } else {
      dispatch({
        type: actionTypes.SET_CONFIRM_SMS,
        payload: true,
      });
    }
  };

  const saveChanges = async (formData) => {
    setSMSError(false);
    // filter out any empty additionalNumber rows
    const newAdditionalNums = [];
    if (formData.alternatePhoneNumbers?.length > 0) {
      for (
        let index = formData.alternatePhoneNumbers.length - 1;
        index >= 0;
        index--
      ) {
        if (
          formData.alternatePhoneNumbers[index].phoneNumber ||
          formData.alternatePhoneNumbers[index].extension
        ) {
          newAdditionalNums.unshift(formData.alternatePhoneNumbers[index]);
        } else {
          remove(index);
        }
      }
    }

    const payload = {
      forwardToPhoneNumber:
        formData.primaryPhoneNumber.unreachableForwardingNumber,
      huntGroupAlternatePhone: {
        alternateNumberList: newAdditionalNums,
      },
      locationId: state.locationId,
      // we don't allow extension updates once a team has an extension assigned, but if a team exists without an extension then a user is allowed to create one
      primaryExtension:
        state.origPrimaryPhoneNumber.extension ||
        formData.primaryPhoneNumber.extension,
      primaryPhoneNumber: formData.primaryPhoneNumber.phoneNumber,
    };

    try {
      await updateHuntGroupPhoneNumber({
        huntGroupId,
        payload,
        locationId: state.locationId,
      });

      removeAvailableNumbers();
      setSnackbar({
        message: 'Success. Phone number settings have been saved.',
        severity: 'success',
      });

      dispatch({
        type: actionTypes.UPDATE_INITIAL_DATA,
        payload: {
          origAlternatePhoneNumbers: newAdditionalNums,
          origPrimaryPhoneNumber: {
            extension:
              state.origPrimaryPhoneNumber.extension ||
              formData.primaryPhoneNumber.extension,
            phoneNumber: formData.primaryPhoneNumber.phoneNumber,
            unreachableForwardingNumber:
              formData.primaryPhoneNumber.unreachableForwardingNumber,
          },
        },
      });

      dispatch({
        type: actionTypes.SET_EXTENSION_ERRORS,
        payload: [],
      });
    } catch (errorObj) {
      handleSaveErrors(errorObj);
    }
  };

  const handleSave = async (formData) => {
    if (
      smsEnabled &&
      teamsSMS &&
      formData.primaryPhoneNumber.phoneNumber !==
        state.origPrimaryPhoneNumber.phoneNumber
    ) {
      showSMSModal(formData.primaryPhoneNumber.phoneNumber);
    } else {
      await saveChanges(formData);
      queryClient.invalidateQueries(['getTeams']);
    }
  };

  const proceedToSave = async () => {
    await saveChanges(getValues());
    queryClient.invalidateQueries(['getTeams']);
    setSmsEnabled(false);
    dispatch({
      type: actionTypes.SET_CONFIRM_SMS,
      payload: false,
    });
    dispatch({
      type: actionTypes.SET_DELETE_PRIMARY_SMS,
      payload: false,
    });
  };

  const cancelChange = () => {
    dispatch({
      type: actionTypes.SET_CONFIRM_SMS,
      payload: false,
    });
  };

  const cancelDelete = () => {
    dispatch({
      type: actionTypes.SET_DELETE_PRIMARY_SMS,
      payload: false,
    });
  };

  const handleRemoveClick = (index) => {
    const phoneNum = getValues(`alternatePhoneNumbers[${index}].phoneNumber`);

    // if the additional number is already saved to the Team, confirm that the user wants to remove it
    if (
      state.origAlternatePhoneNumbers.some(
        (alternate) => alternate.phoneNumber === phoneNum,
      )
    ) {
      dispatch({
        type: actionTypes.SET_REMOVE_MODAL,
        payload: { open: true, numberToRemove: phoneNum, index },
      });
    } else {
      const errorsUpdated = [];
      state.extensionErrors.forEach((error) => {
        if (error.index < index) {
          errorsUpdated[error.index] = error;
        } else if (error.index > index) {
          errorsUpdated[error.index - 1] = {
            error: error.error,
            index: error.index - 1,
          };
        }
      });

      remove(index);

      dispatch({
        type: actionTypes.SET_EXTENSION_ERRORS,
        payload: errorsUpdated,
      });

      // determineCanSave needs to be delayed until slightly after removing the form field in order to calculate the correct value
      setTimeout(() => {
        determineCanSave();
      }, 0);
    }
  };

  const cancelRemoveAlternateNumber = () => {
    dispatch({
      type: actionTypes.SET_REMOVE_MODAL,
      payload: {
        index: null,
        isLoading: false,
        numberToRemove: '',
        open: false,
      },
    });
  };

  const confirmRemoveAlternateNumber = async () => {
    const { numberToRemove, index } = state.removeModal;

    const newAlternateNumbers = state.origAlternatePhoneNumbers.filter(
      (alternate, alternateIndex) => alternateIndex !== index,
    );

    try {
      const payload = {
        forwardToPhoneNumber:
          state.origPrimaryPhoneNumber.unreachableForwardingNumber,
        huntGroupAlternatePhone: {
          alternateNumberList: newAlternateNumbers,
        },
        locationId: state.locationId,
        primaryExtension: state.origPrimaryPhoneNumber.extension,
        primaryPhoneNumber: state.origPrimaryPhoneNumber.phoneNumber,
      };

      dispatch({
        type: actionTypes.SET_REMOVE_MODAL,
        payload: { isLoading: true },
      });

      await HuntGroupService.updateHuntGroupPhoneNumber(
        huntGroupId,
        payload,
        state.locationId,
      );

      const errorsUpdated = [];
      state.extensionErrors.forEach((error) => {
        if (error.index < index) {
          errorsUpdated[error.index] = error;
        } else if (error.index > index) {
          errorsUpdated[error.index - 1] = {
            error: error.error,
            index: error.index - 1,
          };
        }
      });

      remove(index);
      removeAvailableNumbers();

      dispatch({
        type: actionTypes.SET_EXTENSION_ERRORS,
        payload: errorsUpdated,
      });

      dispatch({
        type: actionTypes.UPDATE_AVAILABLE_NUMBERS,
        payload: { prevVal: numberToRemove },
      });

      dispatch({
        type: actionTypes.UPDATE_INITIAL_DATA,
        payload: {
          origAlternatePhoneNumbers: newAlternateNumbers,
        },
      });
      const { primaryPhoneNumber, alternatePhoneNumbers } = getValues();

      // we need to maintain the fieldValues a user may have changed rather than resetting the entire form after removing a single number, but we need to delay briefly until some other re-renders happen first
      setTimeout(() => {
        renderFormFields(primaryPhoneNumber, alternatePhoneNumbers, true);
      }, 200);

      setSnackbar({
        message: `Successfully removed alternate number ${formatToPhoneNumber(
          numberToRemove,
        )}.`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        message: `There was an error while removing the alternate number ${formatToPhoneNumber(
          numberToRemove,
        )}. Please try again later or contact support if the issue persists.`,
        severity: 'error',
      });
    } finally {
      dispatch({
        type: actionTypes.SET_REMOVE_MODAL,
        payload: {
          index: null,
          isLoading: false,
          numberToRemove: '',
          open: false,
        },
      });
    }
  };

  useEffect(() => {
    if (!huntGroupId || availableNumbersLoading || ringPatternsLoading) return;
    getInitialData();
  }, [
    huntGroupId,
    ringPatternsData,
    availableNumbersData,
    availableNumbersLoading,
    ringPatternsLoading,
  ]);

  useEffect(() => {
    renderFormFields();
  }, [state.origAlternatePhoneNumbers, state.origPrimaryPhoneNumber]);

  if (state.isLoading) {
    return <Skeleton />;
  }

  return (
    <FormProvider {...methods}>
      <div style={{ width: 620 }}>
        <ContainerStyles
          id="phone-number"
          onSubmit={handleSubmit(saveChanges)}
          data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-Form"
        >
          <div style={{ paddingBottom: 90 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} className="header-padding">
                <div className="m-settings-header">
                  <h1
                    className="m-settings-header__heading"
                    data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-Heading"
                  >
                    Phone Number
                  </h1>
                </div>
              </Grid>
              {state.origPrimaryPhoneNumber.extension && (
                <div
                  className="platform-modules__info"
                  data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-ModuleInfo"
                >
                  The primary extension for this team is{' '}
                  <strong>{state.origPrimaryPhoneNumber.extension}</strong>. If
                  you would like to be able to dial the team directly via
                  another extension, please click <strong>Add Number</strong>{' '}
                  below and add the desired extension.
                </div>
              )}
              <Grid item xs={12}>
                <span
                  className="Heading5"
                  data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-PrimaryNumber"
                >
                  Primary Number
                </span>
              </Grid>
            </Grid>

            <Grid className="number-container" container spacing={2}>
              <Grid item xs={state.origPrimaryPhoneNumber.extension ? 5 : 4}>
                <Hint
                  showRed={!!smsError}
                  label="PHONE NUMBER"
                  tooltipText="Select a phone number that can be directly dialed to reach this team.
                    If the team does not have a unique phone number assigned
                    then simply enter a unique extension for this team."
                />
                <Controller
                  control={control}
                  name="primaryPhoneNumber.phoneNumber"
                  render={({ field }) => (
                    <SelectSearchInput
                      onChange={(value) => {
                        if (dispatch) {
                          dispatch({
                            type: actionTypes.UPDATE_AVAILABLE_NUMBERS,
                            payload: {
                              newVal: value,
                              prevVal: field.value,
                            },
                          });
                        }
                        setSMSError('');
                        field.onChange(value);
                        determineCanSave();
                      }}
                      field={field}
                      error={smsError}
                      displayOptions={[
                        ...(field.value
                          ? [
                              {
                                label: '',
                                value: '',
                              },
                            ]
                          : []),
                        {
                          label: formatToPhoneNumber(field.value),
                          value: field.value,
                        },
                        ...state.availableNumbers,
                      ]}
                      data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-PhoneNumber"
                    />
                  )}
                />
              </Grid>
              {!state.origPrimaryPhoneNumber.extension && (
                <Grid item xs={2}>
                  <div className="m-phone-number__label-extension">
                    <Controller
                      control={control}
                      name="primaryPhoneNumber.extension"
                      render={({ field, fieldState }) => (
                        <TextField
                          label="extension"
                          id="extensionTextInput"
                          maxLength={6}
                          {...field}
                          onChange={(event) => {
                            field.onChange(event);
                            determineCanSave();
                          }}
                          error={Boolean(fieldState.error)}
                          errorMessage={fieldState.error?.message}
                          data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-TextFieldExtensionInput"
                        />
                      )}
                    />
                  </div>
                </Grid>
              )}
              <Grid item xs={state.origPrimaryPhoneNumber.extension ? 5 : 4}>
                <Hint
                  label="IF NUMBER UNREACHABLE"
                  tooltipText="If your phone ever loses connectivity with Nextiva, for example, a power or internet outage at your office,
                    you can still maintain call continuity. By inputting a phone number or extension in this field, you can have your calls automatically redirected to an alternate destination."
                />
                <div className="m-phone-number__input-unreachable">
                  <Controller
                    control={control}
                    name="primaryPhoneNumber.unreachableForwardingNumber"
                    render={({ field }) => (
                      <TextField
                        id="unreachableForwardingNumberTextInput"
                        maxLength={10}
                        pattern="[0-9\/]*"
                        {...field}
                        onChange={(event) => {
                          field.onChange(event);
                          determineCanSave();
                        }}
                        inputProps={{
                          'data-testid':
                            'PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-UnreachableNumber',
                        }}
                        data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-TextField"
                      />
                    )}
                  />
                </div>
              </Grid>
              <Grid item xs={2} />
            </Grid>

            {teamsSMS && (
              <Grid item xs={12} style={{ marginTop: 50 }}>
                <TeamSMSToggle
                  phoneNumber={state.origPrimaryPhoneNumber.phoneNumber}
                  locationId={state.locationId}
                  setSMSError={setSMSError}
                  smsEnabled={smsEnabled}
                  isLoading={isLoading}
                  setSmsEnabled={setSmsEnabled}
                  proceedToSave={proceedToSave}
                  confirmSMS={state.confirmSMS}
                  deletePrimary={state.deletePrimary}
                  cancelChange={cancelChange}
                  cancelDelete={cancelDelete}
                  huntGroupId={huntGroupId}
                />
                <StyledDivider />
              </Grid>
            )}

            <Grid container spacing={2} style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <span className="Heading5">Additional Numbers</span>
              </Grid>
            </Grid>

            <Grid className="number-container" container spacing={2}>
              {fields.map((item, index) => (
                <Grid
                  container
                  spacing={2}
                  key={item.id}
                  className="additional-numbers-grid"
                >
                  <Grid item xs={4}>
                    <Hint
                      label="ADDITIONAL NUMBERS"
                      tooltipText="Select an alternate phone number that can be directly dialed to reach this team.
                            If the team does not have a unique phone number assigned
                            then simply just enter a unique extension for this team."
                    />
                    <Controller
                      name={`alternatePhoneNumbers[${index}].phoneNumber`}
                      defaultValue={item.phoneNumber}
                      render={({ field }) => (
                        <SelectSearchInput
                          onChange={(value) => {
                            if (dispatch) {
                              dispatch({
                                type: actionTypes.UPDATE_AVAILABLE_NUMBERS,
                                payload: {
                                  newVal: value,
                                  prevVal: field.value,
                                },
                              });
                            }
                            field.onChange(value);
                            determineCanSave();
                          }}
                          field={field}
                          displayOptions={[
                            ...(field.value
                              ? [
                                  {
                                    label: '',
                                    value: '',
                                  },
                                ]
                              : []),
                            {
                              label: formatToPhoneNumber(field.value),
                              value: field.value,
                            },
                            ...state.availableNumbers,
                          ]}
                          data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-SelectSearchInput"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <span className="m-phone-number__label-extension">
                      <Controller
                        name={`alternatePhoneNumbers[${index}].extension`}
                        render={({ field, fieldState }) => (
                          <TextField
                            label="EXTENSION"
                            maxLength={6}
                            {...field}
                            onChange={(event) => {
                              field.onChange(event);
                              determineCanSave();
                            }}
                            error={
                              Boolean(fieldState.error) ||
                              state.extensionErrors?.[index]?.error
                            }
                            errorMessage={
                              fieldState.error?.message
                                ? fieldState.error?.message
                                : state.extensionErrors?.[index]?.error
                            }
                            inputProps={{
                              'data-testid':
                                'PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-AdditionalNumberExtension',
                            }}
                            data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-TextFieldExtension"
                          />
                        )}
                        defaultValue={item.extension}
                        control={control}
                      />
                    </span>
                  </Grid>

                  <Grid item xs={4}>
                    <Hint
                      label="RING TONE"
                      tooltipText="Select an alternate ring pattern."
                    />
                    <Controller
                      name={`alternatePhoneNumbers[${index}].ringPattern`}
                      render={({ field }) => (
                        <RhfSelect
                          field={field}
                          determineCanSave={determineCanSave}
                          options={ringPatterns}
                        />
                      )}
                      defaultValue={item.ringPattern}
                      control={control}
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <Button
                      onClick={() => handleRemoveClick(index)}
                      variant="text"
                      style={{ marginTop: 26 }}
                      data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-RemoveButton"
                    >
                      <StyledIcon icon={Icons.remove} size={18} />
                      <span style={{ color: 'red', marginLeft: 4 }}>
                        Remove
                      </span>
                    </Button>
                  </Grid>
                </Grid>
              ))}

              {fields.length < MAX_ALTERNATE_NUMBERS && (
                <Grid item xs={12}>
                  <Button
                    onClick={addAdditionalNumberRow}
                    variant="text"
                    data-testid="PlatformAdminUI-TeamVoiceSettings-TeamPhoneNumber-AddAdditionalNumber"
                  >
                    <StyledIcon icon={Icons.addCircle} size={18} />
                    <span style={{ color: '#005FEC', marginLeft: 4 }}>
                      Add Number
                    </span>
                  </Button>
                </Grid>
              )}
            </Grid>
          </div>

          <PlatformDialog
            okButtonText="Remove number"
            okClick={confirmRemoveAlternateNumber}
            header="Remove number?"
            open={state.removeModal.open}
            isLoading={state.removeModal.isLoading}
            type="info"
            cancelClick={cancelRemoveAlternateNumber}
          >
            <span>
              You are about to remove{' '}
              <strong>
                {formatToPhoneNumber(state.removeModal.numberToRemove)}
              </strong>{' '}
              from this team&apos;s additional numbers.
              <br />
              <br />
              Don&apos;t worry, you can always add it again if you need to.
            </span>
          </PlatformDialog>

          <SectionSaveCancel
            open={state.canSave}
            cancelOnClick={cancelChanges}
            saveOnClick={handleSubmit(handleSave)}
          />
        </ContainerStyles>
      </div>
    </FormProvider>
  );
};

TeamPhoneNumberContainer.propTypes = {
  huntGroupId: PropTypes.string.isRequired,
};
